import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { StaticDatePicker } from '@mui/x-date-pickers';
import * as Sentry from '@sentry/react';
import { CodesService, PassScheduleSelectionType, PassService, PaymentFormType, PaymentsService } from '../api';
import type { PassDatedSessionSelectionDTO } from '../api/models/PassDatedSessionSelectionDTO';
import type { PassOrderPageAvailabilityOut } from '../api/models/PassOrderPageAvailabilityOut';
import type { PassScheduleSelectionDTO } from '../api/models/PassScheduleSelectionDTO';
import { useManagePasses } from './ManagePassesContext';
import { useLanguage } from '../utils/GeneralUtils';
import { customerFacingPriceFormatter } from '../orderPage/utils';
import { bookingFeeAmount, withBookingFee } from '../orderPage/core/pricing';
import { PassPaymentIframe } from './PassPaymentIframe';
import { toPaymentUrl } from '../orderPage/steps/payments/squarePaymentForm';
import { useStripeRenderProps } from '../orderPage/steps/payments/useStripeRenderProps';
import { getDateFormat } from '../utils/getDateAndTimeFormat';
import { DATE_FORMAT } from '../consts';
import PassScheduleSelector from '../shared/passScheduleSelector/PassScheduleSelector';
import { PassDatedSessionPicker } from '../shared/passDatedSessionPicker/PassDatedSessionPicker';
import { dropUnavailableSelections, resolveRedeemablePages } from '../shared/passScheduleSelector/utils';
import {
  isDatedSessionUnavailableError,
  isSlotCapacityError,
  passPurchaseErrorMessage,
} from '../shared/errors/passPurchaseErrors';
import { applyCodeErrorMessage } from '../shared/errors/applyCodeErrors';
import { getNewestPass, isFixedScheduleMonthly, isRecurringMonthly } from './utils/passUtils';
import { savedCardErrorMessage } from './utils/savedCardErrors';
import { SaveCardConsent } from '../shared/ui/SaveCardConsent';
import { getSaveCardConsentLabel, isSaveCardConsentRequired } from '../utils/saveCardUtils';

export const BuyPassCheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();
  const { passOrderPageId } = useParams<{ passOrderPageId: string }>();
  const [searchParams] = useSearchParams();
  const troubleshoot = searchParams.get('troubleshoot') === 'true';
  const { account, passes, mutate } = useManagePasses();

  const [passId, setPassId] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [voucherCode, setVoucherCode] = useState('');
  const [isLoadingVoucher, setIsLoadingVoucher] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [voucherApplied, setVoucherApplied] = useState(false);

  const [shouldStartLater, setShouldStartLater] = useState(false);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);

  const [scheduleSelections, setScheduleSelections] = useState<PassScheduleSelectionDTO[]>([]);
  const [didPrefillSchedule, setDidPrefillSchedule] = useState(false);
  const [datedSelections, setDatedSelections] = useState<PassDatedSessionSelectionDTO[]>([]);
  const [unavailableDatedSelection, setUnavailableDatedSelection] = useState<
    PassDatedSessionSelectionDTO | undefined
  >();
  const [datedPickerState, setDatedPickerState] = useState({ isLoading: true, isError: false, notEnough: false });

  const [saveCardConsent, setSaveCardConsent] = useState(false);
  const [useDifferentCard, setUseDifferentCard] = useState(false);

  const [availability, setAvailability] = useState<PassOrderPageAvailabilityOut[] | undefined>();
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(false);

  const isRtl = i18n.dir() === 'rtl';
  const passOrderPage = account?.pass_order_pages?.find((p) => p.id === passOrderPageId);
  const customer = passes[0];
  const mustSelectSchedule =
    !!passOrderPage &&
    (passOrderPage.schedule_selection_type === PassScheduleSelectionType.WEEKLY_SLOTS ||
      isFixedScheduleMonthly(passOrderPage.pass_type));
  const mustPickDatedSessions = passOrderPage?.schedule_selection_type === PassScheduleSelectionType.DATED_SESSIONS;
  const datedWindowStart = shouldStartLater && startDate ? startDate.format(DATE_FORMAT) : moment().format(DATE_FORMAT);
  const purchaseStartDate = mustPickDatedSessions
    ? datedWindowStart
    : shouldStartLater && startDate
    ? startDate.format(DATE_FORMAT)
    : undefined;
  const datedWindowEnd = moment(datedWindowStart, DATE_FORMAT)
    .add(passOrderPage?.validity_days ?? 0, 'days')
    .format(DATE_FORMAT);
  // The card is charged off an existing pass of this customer, so the newest one holding a token is
  // the card on file. Recurring passes are excluded: they bill through Grow's direct debit already.
  const savedCardSourcePass = getNewestPass(passes.filter((p) => p.has_saved_card));
  const isSalesTaxLocked = !!account?.payment_integration_settings?.capabilities?.sales_tax_locked;
  const canPayWithSavedCard =
    !!savedCardSourcePass && !!passOrderPage && !isRecurringMonthly(passOrderPage.pass_type) && !isSalesTaxLocked;
  const payingWithSavedCard = canPayWithSavedCard && !useDifferentCard;
  // Paying by card form goes through the same get_payment_form call as the widget, so the same
  // consent is owed. Nothing new is captured when an existing token is charged.
  const mustConsentToSaveCard =
    !payingWithSavedCard && isSaveCardConsentRequired(passOrderPage, account, PaymentFormType.PASS);

  const loadAvailability = useCallback(async () => {
    if (!account || !passOrderPage) return undefined;
    setAvailabilityLoading(true);
    setAvailabilityError(false);
    try {
      const data = await PassService.getPassAvailability(passOrderPage.id, account.id);
      setAvailability(data);
      return data;
    } catch (err) {
      Sentry.captureException(err);
      setAvailabilityError(true);
      return undefined;
    } finally {
      setAvailabilityLoading(false);
    }
  }, [account, passOrderPage]);

  useEffect(() => {
    if (!mustSelectSchedule) return;
    void loadAvailability();
  }, [loadAvailability, mustSelectSchedule]);

  // A slot can fill up between loading the grid and submitting. Refresh it and drop whatever is now
  // full, so the exact-count guard blocks the CTA until the customer has picked again.
  const refreshAfterCapacityRejection = async () => {
    const fresh = await loadAvailability();
    setScheduleSelections((current) => dropUnavailableSelections(current, fresh));
  };

  useEffect(() => {
    if (didPrefillSchedule) return;
    if (!account || !passOrderPage) return;
    if (
      passOrderPage.schedule_selection_type !== PassScheduleSelectionType.WEEKLY_SLOTS &&
      !isFixedScheduleMonthly(passOrderPage.pass_type)
    ) {
      return;
    }

    const priorPassSelections = passes
      .filter((p) => p.order_page_id === passOrderPageId && (p.schedule_selections?.length ?? 0) > 0)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0]?.schedule_selections;

    if (!priorPassSelections?.length) {
      setDidPrefillSchedule(true);
      return;
    }

    const validOptions = new Set(
      resolveRedeemablePages(passOrderPage, account).flatMap(({ orderPageId, weeklyOptions }) =>
        Object.entries(weeklyOptions).flatMap(([weekday, times]) =>
          times.map((startTime) => `${orderPageId}|${weekday}|${startTime}`),
        ),
      ),
    );

    const stillValid = priorPassSelections.filter((sel) =>
      validOptions.has(`${sel.order_page_id}|${sel.weekday}|${sel.start_time}`),
    );

    if (stillValid.length > 0) {
      setScheduleSelections(stillValid);
    }
    setDidPrefillSchedule(true);
  }, [account, passOrderPage, passes, passOrderPageId, didPrefillSchedule]);

  const dropUnavailableDatedSelection = (e: any) => {
    if (!isDatedSessionUnavailableError(e)) return;
    const unavailable = { order_page_id: e.body.order_page_id, date: e.body.date, start_time: e.body.start_time };
    setDatedSelections((current) =>
      current.filter(
        (selection) =>
          !(
            selection.order_page_id === unavailable.order_page_id &&
            selection.date === unavailable.date &&
            selection.start_time === unavailable.start_time
          ),
      ),
    );
    setUnavailableDatedSelection(unavailable);
  };

  const displayPrice = discountedPrice ?? passOrderPage?.price ?? 0;

  const redeemableEntries = (passOrderPage?.redeemable_on || [])
    .map((item) => {
      const op = account?.experiences?.[0]?.order_pages?.find((p: any) => p.id === item.order_page_id);
      const name = op?.label?.[language] || '';
      return name ? { name, limit: item.limit } : null;
    })
    .filter(Boolean) as { name: string; limit?: number | null }[];

  const { stripeAccountId, stripe_integration_publishable_key } = useStripeRenderProps(account);

  const initPayment = useCallback(
    async (startDateStr?: string) => {
      if (!account || !passOrderPage || !customer) {
        return;
      }
      setIsLoadingPayment(true);
      setPaymentError(null);
      try {
        const newPass = await PassService.createPass({
          language: customer.language || language,
          account_id: account.id,
          order_page_id: passOrderPage.id,
          price: passOrderPage.price,
          firstname: customer.firstname,
          lastname: customer.lastname,
          alternative_full_name: customer.alternative_full_name,
          email: customer.email,
          phone: customer.phone,
          ...(startDateStr ? { start_date: startDateStr } : {}),
          ...(scheduleSelections.length ? { schedule_selections: scheduleSelections } : {}),
          ...(datedSelections.length ? { dated_session_selections: datedSelections } : {}),
          save_card_consent_accepted: saveCardConsent,
        });

        setPassId(newPass.id);

        const paymentForm = await PaymentsService.getPaymentForm({
          language: customer.language || language,
          account_id: account.id,
          order_page_id: passOrderPage.id,
          entity_id: newPass.id,
          payment_form_type: PaymentFormType.PASS,
        });

        setPaymentUrl(toPaymentUrl(paymentForm));
      } catch (e: any) {
        if (isSlotCapacityError(e)) {
          await refreshAfterCapacityRejection();
        }
        dropUnavailableDatedSelection(e);
        setPaymentError(passPurchaseErrorMessage(e, t) ?? t('manage_passes.buy.paymentError'));
      } finally {
        setIsLoadingPayment(false);
      }
    },
    [account, passOrderPage, customer, language, t, scheduleSelections, datedSelections, saveCardConsent],
  );

  // Creation and charge are one call here -- no payment form to wait on, so a successful purchase
  // goes back to the dashboard with the new pass already in the list.
  const payWithSavedCard = async () => {
    if (!account || !passOrderPage || !customer || !savedCardSourcePass) {
      return;
    }
    setIsLoadingPayment(true);
    setPaymentError(null);
    try {
      await PassService.createPass({
        language: customer.language || language,
        account_id: account.id,
        order_page_id: passOrderPage.id,
        price: passOrderPage.price,
        firstname: customer.firstname,
        lastname: customer.lastname,
        alternative_full_name: customer.alternative_full_name,
        email: customer.email,
        phone: customer.phone,
        ...(purchaseStartDate ? { start_date: purchaseStartDate } : {}),
        ...(scheduleSelections.length ? { schedule_selections: scheduleSelections } : {}),
        ...(datedSelections.length ? { dated_session_selections: datedSelections } : {}),
        pay_with_saved_card: true,
        saved_card_source_pass_id: savedCardSourcePass.id,
        saved_card_source_pass_order_number: savedCardSourcePass.order_number,
      });
      await mutate();
      navigate('/passes/dashboard');
    } catch (e: any) {
      Sentry.captureException(e);
      if (isSlotCapacityError(e)) {
        await refreshAfterCapacityRejection();
      }
      dropUnavailableDatedSelection(e);
      setPaymentError(passPurchaseErrorMessage(e, t) ?? savedCardErrorMessage(e, t));
    } finally {
      setIsLoadingPayment(false);
    }
  };

  // A Square checkout token expires after two hours, so a form left open that long is requested again.
  const refreshPaymentForm = async () => {
    if (!account || !passOrderPage || !customer || !passId) {
      return;
    }
    setIsLoadingPayment(true);
    setPaymentError(null);
    try {
      const paymentForm = await PaymentsService.getPaymentForm({
        language: customer.language || language,
        account_id: account.id,
        order_page_id: passOrderPage.id,
        entity_id: passId,
        payment_form_type: PaymentFormType.PASS,
      });
      setPaymentUrl(toPaymentUrl(paymentForm));
    } catch (e: any) {
      Sentry.captureException(e);
      setPaymentError(t('manage_passes.buy.paymentError'));
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleContinueToPayment = () => {
    void initPayment(purchaseStartDate);
  };

  const handleApplyCode = async () => {
    if (!passId || !voucherCode.trim()) return;
    setIsLoadingVoucher(true);
    setVoucherError(null);
    setVoucherApplied(false);

    try {
      const resp = await CodesService.applyCode({ entity_id: passId, code: voucherCode.trim() });
      if (resp.new_price === 0) {
        navigate('/passes/dashboard');
        return;
      }
      if (resp.payment_link) {
        setPaymentUrl(resp.payment_link);
      }
      if (resp.new_price) {
        setDiscountedPrice(resp.new_price);
      }
      setVoucherApplied(true);
    } catch (e: any) {
      setVoucherError(applyCodeErrorMessage(e, t));
    } finally {
      setIsLoadingVoucher(false);
    }
  };

  if (!account || !passOrderPage) {
    return null;
  }

  return (
    <Grid container flexDirection="column" gap={2.5} pb={4}>
      <Grid container alignItems="center" gap={1}>
        <IconButton onClick={() => navigate('/passes/buy')} size="small" edge="start">
          {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {passOrderPage.label?.[language] || t('manage_passes.pass.unnamed')}
        </Typography>
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container flexDirection="column" gap={1.5}>
          <Grid container gap={1}>
            <Grid
              item
              sx={{ flex: 1, textAlign: 'center', py: 1.5, px: 1, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('pass.num_punches')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {passOrderPage.num_punches ?? t('pass.unlimited')}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{ flex: 1, textAlign: 'center', py: 1.5, px: 1, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('pass.validity_days')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('pass.days', { count: passOrderPage.validity_days })}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {redeemableEntries.length > 0
                ? `${t('manage_passes.pass.redeemableOn')} ${redeemableEntries
                    .map(({ name, limit }) =>
                      limit != null ? `${name} ${t('pass.punch_limit', { count: limit })}` : name,
                    )
                    .join(', ')}`
                : t('manage_passes.pass.redeemableOnAll')}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {customerFacingPriceFormatter(withBookingFee(displayPrice, account), account)}
              {isRecurringMonthly(passOrderPage.pass_type) && ` ${t('manage_passes.buy.perMonth')}`}
            </Typography>
          </Grid>
          {bookingFeeAmount(displayPrice, account) > 0 && (
            <Typography variant="caption" color="text.secondary" textAlign="end">
              {t('manage_passes.buy.includesBookingFee', {
                fee: customerFacingPriceFormatter(bookingFeeAmount(displayPrice, account), account),
              })}
            </Typography>
          )}
        </Grid>
      </Paper>

      {!passId && mustSelectSchedule && (
        <Paper elevation={0} sx={{ borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Grid container flexDirection="column" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('pass_schedule.heading')}
            </Typography>
            {!isFixedScheduleMonthly(passOrderPage.pass_type) && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('pass_schedule.subheading')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pass_schedule.changeableLater')}
                </Typography>
              </>
            )}
            {availabilityError && <Alert severity="error">{t('pass_schedule.errors.availabilityLoadFailed')}</Alert>}
            <PassScheduleSelector
              passOrderPage={passOrderPage}
              account={account}
              value={scheduleSelections}
              onChange={setScheduleSelections}
              maxSelections={passOrderPage.weekly_session_count ?? undefined}
              availability={availability}
              troubleshoot={troubleshoot}
            />
            {passOrderPage.weekly_session_count != null ? (
              <Typography
                variant="caption"
                color={
                  scheduleSelections.length === passOrderPage.weekly_session_count ? 'success.main' : 'text.secondary'
                }>
                {t('pass_schedule.counter', {
                  selected: scheduleSelections.length,
                  required: passOrderPage.weekly_session_count,
                })}
              </Typography>
            ) : (
              scheduleSelections.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('pass_schedule.selectAtLeastOne')}
                </Typography>
              )
            )}
            {passOrderPage.weekly_session_count != null &&
              scheduleSelections.length !== passOrderPage.weekly_session_count && (
                <Typography variant="caption" color="text.secondary">
                  {t('pass_schedule.exactCountRequired', {
                    count: passOrderPage.weekly_session_count,
                  })}
                </Typography>
              )}
          </Grid>
        </Paper>
      )}

      {!passId && mustPickDatedSessions && (
        <Paper elevation={0} sx={{ borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Grid container flexDirection="column" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('pass_dated_sessions.heading')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pass_dated_sessions.subheading', { count: passOrderPage.num_punches })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pass_dated_sessions.changeableLater')}
            </Typography>
            <PassDatedSessionPicker
              account={account}
              passOrderPage={passOrderPage}
              startDate={datedWindowStart}
              endDate={datedWindowEnd}
              value={datedSelections}
              onChange={(next) => {
                setDatedSelections(next);
                setUnavailableDatedSelection(undefined);
              }}
              unavailableSelection={unavailableDatedSelection}
              onStateChange={setDatedPickerState}
            />
          </Grid>
        </Paper>
      )}

      {!passId && (
        <Paper elevation={0} sx={{ borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Grid container flexDirection="column" gap={1.5}>
            {!isRecurringMonthly(passOrderPage.pass_type) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldStartLater}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setShouldStartLater(newValue);
                      if (!newValue) {
                        setStartDate(null);
                      }
                    }}
                    disabled={isLoadingPayment}
                  />
                }
                label={t('manage_passes.buy.startDateToggleLabel')}
              />
            )}
            {!isRecurringMonthly(passOrderPage.pass_type) && shouldStartLater && (
              <Grid container flexDirection="column" gap={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('manage_passes.buy.startDatePickerLabel')}
                    value={startDate ? startDate.format('DD/MM/YYYY') : ''}
                    onClick={() => !isLoadingPayment && setIsStartDatePickerOpen(true)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Modal
                    open={isStartDatePickerOpen}
                    onClose={() => setIsStartDatePickerOpen(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Paper sx={{ p: 1, borderRadius: 2 }}>
                      <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        openTo="day"
                        disablePast
                        value={startDate}
                        minDate={moment()}
                        maxDate={moment().add(6, 'months')}
                        onChange={(date) => {
                          setStartDate(date);
                          setIsStartDatePickerOpen(false);
                        }}
                      />
                    </Paper>
                  </Modal>
                </Grid>
                {startDate && passOrderPage.validity_days != null && (
                  <Typography variant="body2" color="text.secondary">
                    {t('manage_passes.buy.startDatePreview', {
                      start: startDate.format('DD/MM/YYYY'),
                      end: moment(startDate).add(passOrderPage.validity_days, 'days').format('DD/MM/YYYY'),
                    })}
                  </Typography>
                )}
              </Grid>
            )}
            {isRecurringMonthly(passOrderPage.pass_type) && (
              <Typography variant="caption" color="text.secondary">
                {t('manage_passes.buy.recurringDisclaimer')}
              </Typography>
            )}
            {mustConsentToSaveCard && (
              <SaveCardConsent
                checked={saveCardConsent}
                onChange={setSaveCardConsent}
                label={getSaveCardConsentLabel(passOrderPage, language)}
                isRecurring={isRecurringMonthly(passOrderPage.pass_type)}
              />
            )}
            <Grid container justifyContent="flex-end" alignItems="center" gap={1}>
              {payingWithSavedCard && (
                <Button
                  variant="text"
                  size="small"
                  disabled={isLoadingPayment}
                  onClick={() => setUseDifferentCard(true)}>
                  {t('manage_passes.buy.useDifferentCard')}
                </Button>
              )}
              <Button
                variant="contained"
                disabled={
                  isLoadingPayment ||
                  (mustConsentToSaveCard && !saveCardConsent) ||
                  (shouldStartLater && !startDate) ||
                  (mustSelectSchedule && (availabilityLoading || availabilityError)) ||
                  (mustSelectSchedule &&
                    (passOrderPage.weekly_session_count != null
                      ? scheduleSelections.length !== passOrderPage.weekly_session_count
                      : scheduleSelections.length === 0)) ||
                  (mustPickDatedSessions &&
                    (datedPickerState.isLoading ||
                      datedPickerState.isError ||
                      datedPickerState.notEnough ||
                      datedSelections.length !== passOrderPage.num_punches))
                }
                onClick={payingWithSavedCard ? payWithSavedCard : handleContinueToPayment}>
                {isLoadingPayment ? (
                  <CircularProgress size={20} />
                ) : payingWithSavedCard ? (
                  t('manage_passes.buy.payWithSavedCard', {
                    price: customerFacingPriceFormatter(withBookingFee(displayPrice, account), account),
                  })
                ) : (
                  t('manage_passes.buy.continueToPayment')
                )}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {!payingWithSavedCard && !isRecurringMonthly(passOrderPage.pass_type) && (
        <TextField
          size="small"
          value={voucherCode}
          onChange={(e) => setVoucherCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleApplyCode()}
          placeholder={t('manage_passes.buy.voucherCode')}
          disabled={!passId || isLoadingVoucher}
          error={!!voucherError}
          helperText={voucherError || (voucherApplied ? t('manage_passes.buy.voucherApplied') : undefined)}
          FormHelperTextProps={{ sx: { color: voucherApplied ? 'success.main' : undefined } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={handleApplyCode}
                  disabled={!passId || !voucherCode.trim() || isLoadingVoucher}>
                  {isLoadingVoucher ? <CircularProgress size={16} /> : t('manage_passes.buy.applyCode')}
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
        />
      )}

      {paymentError && <Alert severity="error">{paymentError}</Alert>}

      {passId && isLoadingPayment && (
        <Grid container justifyContent="center" py={4}>
          <CircularProgress />
        </Grid>
      )}

      {!isLoadingPayment &&
        paymentUrl &&
        isRecurringMonthly(passOrderPage.pass_type) &&
        stripe_integration_publishable_key && (
          <Typography variant="body2">
            {t('manage_passes.buy.nextCharge', {
              date: moment().add(1, 'month').format(getDateFormat(account).longDateFormat),
            })}
          </Typography>
        )}

      {!isLoadingPayment && paymentUrl && (
        <PassPaymentIframe
          paymentUrl={paymentUrl}
          stripeAccountId={stripeAccountId}
          stripe_integration_publishable_key={stripe_integration_publishable_key}
          paymentIframeSettings={account.payment_integration_settings?.payment_iframe_settings}
          businessName={account.label?.[language] || account.name}
          payLabelKey={isRecurringMonthly(passOrderPage.pass_type) ? 'manage_passes.buy.payAndStart' : undefined}
          nextChargeDate={
            isRecurringMonthly(passOrderPage.pass_type)
              ? moment().add(1, 'month').format(getDateFormat(account).longDateFormat)
              : undefined
          }
          onFormExpired={() => {
            void refreshPaymentForm();
          }}
        />
      )}
    </Grid>
  );
};
